import { Save } from 'lucide';

//common
import Icon from 'common/components/Icon';
import SchemaForm from 'common/SchemaForm';
import { EMAIL_REGEX, PHONE_INPUT_NUMBER_REGEX, POSITIVE_NUMBER_REGEX } from 'config/regex';
import { genders, jobTitles, organizationTypes } from 'enum/combobox';
import { datePickerCustomStyle } from 'common/SchemaForm/element/datepicker-element/utils';

// screen
import { type UserDetail } from 'modules/account/services/useGetAccountInfo';
import { useUserInfoData } from 'modules/user-info/detail/useUserInfoData';

const UserInfoTab = () => {
  const { userInfo, handleUpdateUserInfo, setUserData, isClient } = useUserInfoData();

  return (
    <SchemaForm<UserDetail>
      formHookProps={{ values: userInfo }}
      globalProps={{ readOnly: isClient }}
      onSubmit={handleUpdateUserInfo}
      schema={{
        fields: ({
          formProps: {
            intl: { formatMessage },
            readOnly,
          },
          methods: { setValue },
        }) => {
          return {
            name: {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.fullname' }),
              placeholder: formatMessage({ id: 'account.enter.fullname' }),
              rules: {
                required: true,
                minLength: {
                  value: 3,
                  message: formatMessage({ id: 'account.error.invalidFullname' }),
                },
              },
              onChange: (event) => {
                setValue('name', event.target.value);
                setUserData('name', event.target.value);
              },
            },
            'userAuthen.userActualManagingOrgType': {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.orgType' }),
              placeholder: formatMessage({ id: 'account.select.orgType' }),
              rules: { required: true },
              options: organizationTypes,
              rawOptions: true,
              disableClearBtn: true,
              disabled: true,
            },
            'miscData.Gender': {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.gender' }),
              placeholder: formatMessage({ id: 'account.select.gender' }),
              options: genders,
              rawOptions: true,
              disableClearBtn: true,
              onChange: (gender) => {
                setUserData('miscData.Gender', gender);
              },
            },
            'userAuthen.userActualManagingOrgId': {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.unitName' }),
              placeholder: formatMessage({ id: 'account.select.unit' }),
              rules: { required: true },
              options: [
                {
                  id: userInfo.userAuthen?.userActualManagingOrgId || '',
                  name: userInfo.userAuthen?.userActualManagingOrgName || '',
                },
              ],
              rawOptions: true,
              disableClearBtn: true,
              disabled: true,
            },
            'miscData.Dob': {
              mode: 'datePicker',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.dob' }),
              placeholder: formatMessage({ id: 'enter' }),
              rules: { required: true },
              onChange: (pickedDate) => {
                setUserData('miscData.Dob', pickedDate);
              },
              disableFuture: true,
              ...datePickerCustomStyle,
            },
            title: {
              mode: 'select',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.jobTitle' }),
              placeholder: formatMessage({ id: 'account.select.jobTitle' }),
              rules: { required: true },
              options: jobTitles.filter(
                ({ organizationTypeId }) =>
                  organizationTypeId ===
                  (userInfo && userInfo.userAuthen
                    ? userInfo.userAuthen.userActualManagingOrgType
                    : '')
              ),
              rawOptions: true,
              disableClearBtn: true,
              onChange: (selectedValue) => {
                setUserData('title', selectedValue);
              },
              disabled: true,
            },
            'miscData.IdDoc': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.identityNumber' }),
              placeholder: formatMessage({ id: 'account.enter.CCCD' }),
              rules: {
                pattern: {
                  value: POSITIVE_NUMBER_REGEX,
                  message: formatMessage({ id: 'account.invalid.cccd' }),
                },
              },
              showRequiredAsterisk: false,
              onChange: (event) => {
                setValue('miscData.IdDoc', event.target.value);
                setUserData('miscData.IdDoc', event.target.value);
              },
            },
            'miscData.JobPosition': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.jobPosition' }),
              placeholder: formatMessage({ id: 'account.enter.jobPosition' }),
              rules: { required: true },
              onChange: (event) => {
                setValue('miscData.JobPosition', event.target.value);
                setUserData('miscData.JobPosition', event.target.value);
              },
            },
            'miscData.IdDocIssuedBy': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.issuePlace' }),
              placeholder: formatMessage({
                id: 'account.enter.cccdIssuedPlace',
              }),
              onChange: (event) => {
                setValue('miscData.IdDocIssuedBy', event.target.value);
                setUserData('miscData.IdDocIssuedBy', event.target.value);
              },
            },
            'miscData.Address': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.address' }),
              placeholder: formatMessage({ id: 'account.enter.address' }),
              rules: { required: true },
              onChange: (event) => {
                setValue('miscData.Address', event.target.value);
                setUserData('miscData.Address', event.target.value);
              },
            },
            'miscData.IdDocIssuedAt': {
              mode: 'datePicker',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.issueDate' }),
              placeholder: formatMessage({ id: 'enter' }),
              onChange: (pickedDate) => {
                setUserData('miscData.IdDocIssuedAt', pickedDate);
              },
              disableFuture: true,
              ...datePickerCustomStyle,
            },
            'miscData.Phone': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.phoneNumber' }),
              placeholder: formatMessage({ id: 'account.enter.phoneNumber' }),
              rules: {
                required: true,
                pattern: {
                  value: PHONE_INPUT_NUMBER_REGEX,
                  message: formatMessage({ id: 'account.invalid.phoneNumber' }),
                },
              },
              onChange: (event) => {
                setValue('miscData.Phone', event.target.value);
                setUserData('miscData.Phone', event.target.value);
              },
            },
            email: {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'email' }),
              placeholder: formatMessage({ id: 'account.enter.email' }),
              rules: {
                required: true,
                pattern: {
                  value: EMAIL_REGEX,
                  message: formatMessage({ id: 'invalid_email' }),
                },
              },
              onChange: (event) => {
                setValue('email', event.target.value);
                setUserData('email', event.target.value);
              },
            },
            'userAuthen.expiredAt': {
              mode: 'datePicker',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.expiredAt' }),
              placeholder: formatMessage({ id: 'enter' }),
              rules: {
                required: userInfo?.userAuthen && userInfo.userAuthen.expiredAt,
              },
              disabled: true,
              ...datePickerCustomStyle,
            },
            'userAuthen.username': {
              mode: 'text-field',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'account.accountName' }),
              placeholder: formatMessage({ id: 'account.enter.accountName' }),
              rules: { required: true },
              disabled: true,
            },
            status: {
              mode: 'switch',
              propsWrapper: { tablet: 6 },
              label: formatMessage({ id: 'statusAdmin' }),
              labelPlacement: 'top',
              onChange: (status) => {
                setUserData('status', status);
                setUserData('userAuthen.inactive', !status);
              },
            },
            blank1: { mode: 'node' },
            submit: {
              mode: 'button',
              type: 'submit',
              variant: 'contained',
              startIcon: <Icon icon={Save} size={16} />,
              hidden: readOnly,
              children: formatMessage({ id: 'save' }),
              propsWrapper: {
                mobile: undefined,
                display: 'flex',
                justifyContent: 'end',
                width: '100%',
              },
            },
          };
        },
      }}
    />
  );
};
export default UserInfoTab;
